// GRYP · lecture des vidéos en boucle muette (hero de la home, teasers des drops).
// Deux cas :
//  a. nos propres MP4 hébergés dans Fichiers (fichiers génériques, résolution d'origine) :
//     <video data-gryp-video data-files='{"full":{"av1":"…","h264":"…"},"light":{"av1":"…","h264":"…"}}' [data-files-landscape='{…}']>
//     → <source> AV1 en premier (le navigateur l'ignore s'il ne le décode pas), H.264 en repli ;
//       « full » (pleine résolution) par défaut, « light » (720p) seulement si la connexion annonce 2g/3g ou l'économie de données ;
//  b. une vidéo Shopify (repli) : <video data-gryp-video data-sources="…" [data-sources-landscape="…"]>,
//     sources rendues par snippets/gryp-video-sources.liquid.
// Le poster (image) reste affiché tant que la vidéo ne joue pas ; « is-playing » est alors posé sur la vidéo
// et sur l'ancêtre [data-gryp-video-root]. Rien n'est chargé si l'utilisateur demande moins d'animations.
// Vidéo Shopify, qualité maximale :
//  1. flux adaptatif HLS dans le lecteur d'Apple (Safari, iOS) ;
//  2. sinon MP4 1080p ;
//  3. MP4 720p seulement si la connexion annonce 2g/3g ou l'économie de données.
// Les données « a » priment sur « b » quand les deux sont présentes.
(() => {
  if (window.GrypVideo) return;

  const parse = (json) => {
    try { return JSON.parse(json || '[]').filter((s) => s && s.u); } catch (e) { return []; }
  };

  const isSlow = () => {
    const c = navigator.connection;
    return !!c && (c.saveData || /(^|-)(2g|3g)$/.test(c.effectiveType || ''));
  };

  // Cas a : nos fichiers MP4, en <source> (AV1 puis H.264). Un fichier absent fait passer à la source suivante.
  const AV1 = 'video/mp4; codecs="av01.0.08M.08"';
  const pickFiles = (json) => {
    let files = {};
    try { files = JSON.parse(json || '{}'); } catch (e) {}
    if (!files.full) return null;
    const list = [];
    const add = (set, label) => {
      if (!set) return;
      if (set.av1) list.push({ u: set.av1, t: AV1, l: `${label}-av1` });
      if (set.h264) list.push({ u: set.h264, t: 'video/mp4', l: `${label}-h264` });
    };
    if (isSlow()) add(files.light, 'fichier-leger');
    add(files.full, 'fichier');
    return list.length ? { sources: list } : null;
  };

  // 720p : Liquid n'expose que le HLS et le MP4 1080p, et le débit de chaque version dépend de la vidéo.
  // On lit donc le nom de la version 720p dans le manifeste HLS (quelques centaines d'octets), puis on vise le MP4 du même nom.
  const find720 = async (hls, best) => {
    try {
      const text = await (await fetch(hls.u)).text();
      const m = text.match(/(HD-720p)\.([\d.]+Mbps)\.hls\.(\d+)_\.m3u8/);
      if (!m) return null;
      const u = best.u.replace(/\.HD-1080p-[\d.]+Mbps-\d+\.mp4/, `.${m[1]}-${m[2]}-${m[3]}.mp4`);
      return u !== best.u ? { f: 'mp4', h: 720, u } : null;
    } catch (e) {
      return null;
    }
  };

  const pick = async (sources, video) => {
    const hls = sources.find((s) => s.f === 'm3u8');
    const mp4 = sources.filter((s) => s.f === 'mp4').sort((a, b) => a.h - b.h);
    const best = mp4.filter((s) => s.h <= 1080).pop() || mp4[0];
    // HLS réservé au lecteur d'Apple (API AirPlay) : le HLS natif de Chrome redescend en 720p/480p en cours de lecture.
    if (hls && 'WebKitPlaybackTargetAvailabilityEvent' in window && video.canPlayType('application/vnd.apple.mpegurl')) return { pick: hls };
    if (isSlow() && hls && best && best.h > 720) {
      const low = await find720(hls, best);
      if (low) return { pick: low, fallback: best };
    }
    return { pick: best };
  };

  const label = (s) => s.l || (s.f === 'm3u8' ? 'hls' : `mp4-${s.h}p`);

  const hasLandscape = (video) => !!(video.dataset.filesLandscape || video.dataset.sourcesLandscape);

  const load = async (video) => {
    const landscape = hasLandscape(video) && matchMedia('(orientation: landscape)').matches;
    const set = landscape ? 'landscape' : 'default';
    if (video.dataset.set === set) return;
    video.dataset.set = set;
    const d = video.dataset;
    const files = landscape ? d.filesLandscape || (d.sourcesLandscape ? '' : d.files) : d.files;
    const result = pickFiles(files) || (await pick(parse(landscape ? d.sourcesLandscape || d.sources : d.sources), video));
    if (!result || video.dataset.set !== set) return;
    if (result.sources) {
      video.removeAttribute('src');
      video.onerror = null;
      video.replaceChildren(...result.sources.map((f) => Object.assign(document.createElement('source'), { src: f.u, type: f.t })));
      video.addEventListener('loadedmetadata', () => {
        const f = result.sources.find((x) => video.currentSrc.endsWith(x.u.replace(/^https?:/, '')) || video.currentSrc === x.u);
        video.dataset.source = f ? f.l : 'fichier';
      }, { once: true });
      video.load();
      video.play().catch(() => {});
      return;
    }
    const { pick: chosen, fallback } = result;
    if (!chosen) return;
    video.replaceChildren();
    video.dataset.source = label(chosen);
    video.onerror = fallback ? () => { video.onerror = null; video.dataset.source = label(fallback); video.src = fallback.u; video.play().catch(() => {}); } : null;
    video.src = chosen.u;
    video.play().catch(() => {});
  };

  const init = (video) => {
    if (video.dataset.grypVideoReady) return;
    video.dataset.grypVideoReady = 'true';
    const root = video.closest('[data-gryp-video-root]');
    video.addEventListener('playing', () => { video.classList.add('is-playing'); root && root.classList.add('is-playing'); });
    load(video);
    if (hasLandscape(video)) matchMedia('(orientation: landscape)').addEventListener('change', () => load(video));
  };

  const initAll = () => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.querySelectorAll('video[data-gryp-video]').forEach(init);
  };

  window.GrypVideo = { initAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  // Sections rechargées dans l'éditeur de thème
  document.addEventListener('shopify:section:load', initAll);
})();
